import type { ScholarshipClass, ScholarshipPlayer } from '../../../../types/scholarships';
import { effectiveClass } from './scholarshipClasses';
import { positionToFilter, type PositionFilter } from './positionGroups';

/**
 * Whether a roster row survives the sidebar's position and class filters.
 *
 * Shared by the Players and Depth chart sub-tabs, which show the same roster through the same
 * two filters and have to agree about who is in it. They each held their own copy of this
 * predicate, and the copies drifted: both read `player.class` directly, so an attached
 * prospect — whose class lives on the linked player record, not on the row — was filtered out
 * of the board while the Class column beside it displayed the very year being filtered for.
 * One predicate reading through {@link effectiveClass} is what keeps the two honest.
 *
 * An empty filter list means "no filter", not "match nothing". A row with no class at all is
 * excluded once a class filter is on, which is the same answer the position side gives: it
 * cannot satisfy a fact it does not carry.
 */
export const matchesRosterFilters = (
  player: ScholarshipPlayer,
  positionFilters: PositionFilter[],
  classFilters: ScholarshipClass[],
): boolean => {
  if (
    positionFilters.length > 0 &&
    !positionFilters.includes(positionToFilter(player.position))
  ) {
    return false;
  }

  if (classFilters.length > 0) {
    const playerClass = effectiveClass(player);
    if (playerClass == null || !classFilters.includes(playerClass)) return false;
  }

  return true;
};
